import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Short label shown on the fallback UI — e.g. "Admin panel" or "Chat history". */
  label?: string;
  /** Optional extra callback for logging to an external monitor (App Insights, Sentry). */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Error boundary for React subtrees. Hook components cannot catch render
 * errors, so this remains a class component — that's a React constraint,
 * not a style preference.
 *
 * Used at the admin-tab level so one misbehaving panel (a crashing
 * DocumentPipeline fetch, for example) doesn't take down the whole
 * admin console. Without this boundary, AdminDashboard.tsx would
 * unmount on any child throw and leave the user with a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ErrorBoundary] ${this.props.label ?? 'component'}:`, error, info);
    this.props.onError?.(error, info);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          padding: '2rem',
          margin: '1rem',
          border: '1px solid #DC3545',
          borderRadius: '6px',
          background: '#F8D7DA',
          color: '#212529',
          maxWidth: '600px',
        }}
      >
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#721C24' }}>
          {this.props.label ? `${this.props.label} failed to load` : 'Something went wrong'}
        </h3>
        <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
          The rest of the console is still working. You can retry this panel or
          switch to another tab. If the problem persists, refresh the page.
        </p>
        <details style={{ marginBottom: '1rem', fontSize: '0.8rem' }}>
          <summary style={{ cursor: 'pointer', color: '#495057' }}>Technical details</summary>
          <pre style={{ margin: '0.5rem 0 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {this.state.error.message}
          </pre>
        </details>
        <button
          onClick={this.reset}
          style={{
            padding: '0.5rem 1rem',
            background: 'var(--doyle-blue)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }
}
